import {graphql} from 'gatsby'
import React from 'react'
import styled from 'styled-components'

import {QuoteCard} from '@/components/quote-card'
import {Text} from '@/components/text'
import g2Logo from '@/images/icons/g2-logo.svg'
import trustpilotLogo from '@/images/icons/trustpilot-logo.svg'
import {MasonryLayout} from '@/sections/masonry-layout'
import {BackgroundWrapper, Section} from '@/styles/grid'
import mq from '@helios/website/src/utils/media-query'

import type {ReviewsGridSliceFragment} from '@/graphql-types'
import type {BackgroundColor} from '@/utils/is-valid-background-color'

const reviewOriginMap = {
    Trustpilot: {
        icon: trustpilotLogo,
        label: 'Trustpilot',
    },
    G2: {
        icon: g2Logo,
        label: 'G2',
    },
}

type Props = {
    slice: ReviewsGridSliceFragment
}

export const ReviewsGridSlice = ({slice}: Props) => {
    const {
        primary: {headline, variant, author_subtext},
        items: reviews,
    } = slice

    return (
        <BackgroundWrapper $color={variant as BackgroundColor}>
            <Section>
                <Headline $variant="heading-large">{headline}</Headline>
                <MasonryLayout>
                    {reviews.map(
                        (
                            {
                                headline,
                                description,
                                number_of_stars,
                                cta_link,
                                author,
                                review_origin,
                            },
                            index,
                        ) => {
                            const origin =
                                reviewOriginMap[review_origin as keyof typeof reviewOriginMap]

                            return (
                                <QuoteCard
                                    key={`quote-card-${index}`}
                                    headline={headline}
                                    description={description}
                                    numberOfStars={number_of_stars}
                                    ctaLink={cta_link}
                                    author={author}
                                    subtitle={`${author_subtext ? `${author_subtext} ` : ''}${
                                        origin.label
                                    }`}
                                    bgColor={variant === 'default' ? 'grey' : 'white'}
                                    Image={(props) => (
                                        <img
                                            {...props}
                                            src={origin.icon}
                                            alt={`${origin.label} icon`}
                                        />
                                    )}
                                />
                            )
                        },
                    )}
                </MasonryLayout>
            </Section>
        </BackgroundWrapper>
    )
}

const Headline = styled(Text)`
    margin-bottom: ${({theme}) => theme.spacings.s40};

    ${mq.min('md')} {
        max-width: 600px;
        margin-bottom: 80px;
    }
`

export const reviewsGridSliceFragment = graphql`
    fragment ReviewsGridSlice on PrismicSlicePageDataBodyReviewsGrid {
        __typename
        primary {
            headline
            variant
            author_subtext
        }
        items {
            headline
            description
            number_of_stars
            review_origin
            author
            cta_link {
                ...PrismicLink
            }
        }
    }
`
